import React from 'react';
import { Info, Clock, Terminal, Code, Globe, Database, AlertTriangle, Calendar } from 'lucide-react';

/**
 * Информационный компонент о Unix-времени и его применении
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-cyan-500" />
        Всё о Unix-времени: принципы, использование, особенности
      </h2>
      
      <div className="prose prose-cyan dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-cyan-500" />
          Что такое Unix-время?
        </h3>
        <p>
          <strong>Unix-время</strong> (также известное как Unix timestamp, POSIX-время или epoch time) — система измерения времени, используемая в компьютерных системах. Она определяется как количество секунд, прошедших с полуночи 1 января 1970 года по UTC, без учета високосных секунд.
        </p>
        <p>
          Точка отсчета (00:00:00 UTC 1 января 1970 года) называется «Unix Epoch» и была выбрана в основном по историческим причинам, так как это было примерное время начала разработки операционной системы Unix.
        </p>

        <h3 className="flex items-center gap-2">
          <Terminal className="w-5 h-5 text-cyan-500" />
          Особенности и ограничения Unix-времени
        </h3>
        <ul>
          <li><strong>32-битное ограничение:</strong> В 32-битных системах Unix-время хранится как знаковое 32-битное число, что означает максимальное значение 2,147,483,647. Это ограничение приведет к проблеме переполнения (так называемая <a href="#" className="text-cyan-600 dark:text-cyan-400 no-underline hover:underline">проблема 2038 года</a>), когда значение достигнет предела в 03:14:07 UTC 19 января 2038 года.</li>
          <li><strong>64-битное представление:</strong> Для преодоления проблемы 2038 года многие современные системы перешли на 64-битное представление, которое имеет гораздо большее ограничение (до 292 миллиардов лет).</li>
          <li><strong>Нет учета високосных секунд:</strong> Unix-время не учитывает високосные секунды, добавляемые для синхронизации с вращением Земли, что может вызывать расхождения с системами точного времени (например, UTC).</li>
          <li><strong>Часовые пояса:</strong> Unix-время всегда указывается в UTC, без учета часовых поясов. Для получения локального времени необходимо применить смещение часового пояса.</li>
          <li><strong>Миллисекунды:</strong> Стандартное Unix-время считается в секундах, но многие современные системы и языки программирования используют миллисекунды или даже микросекунды.</li>
        </ul>

        <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-100 dark:border-cyan-800 my-6">
          <h4 className="text-cyan-700 dark:text-cyan-300 font-medium">Почему именно 1 января 1970 года?</h4>
          <p className="text-cyan-600 dark:text-cyan-400 text-sm mb-0">
            Дата 1 января 1970 года была выбрана создателями Unix (Кеном Томпсоном и Деннисом Ритчи из Bell Labs) в основном по практическим соображениям — это было примерное время начала разработки системы, а также круглая дата. Изначально Unix-время могло представлять даты и до 1970 года (отрицательные значения), но это вызывало проблемы в некоторых системах.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Code className="w-5 h-5 text-cyan-500" />
          Применение Unix-времени в программировании
        </h3>
        <p>
          Unix-время широко используется в программировании и компьютерных системах по нескольким причинам:
        </p>
        <ul>
          <li><strong>Универсальность:</strong> Одинаковое значение Unix-времени соответствует одному и тому же моменту времени везде в мире, независимо от часового пояса.</li>
          <li><strong>Компактность:</strong> Хранение времени в виде одного числа требует меньше места, чем полное представление даты и времени.</li>
          <li><strong>Простота расчетов:</strong> Легко вычислять разницу между датами, добавлять или вычитать интервалы времени.</li>
          <li><strong>Сортировка:</strong> Даты в формате Unix-времени легко сортировать в хронологическом порядке.</li>
        </ul>

        <h4 className="mt-4">Примеры использования в разных языках программирования:</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 mb-6">
          <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
            <div className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-1">JavaScript:</div>
            <pre className="text-xs overflow-x-auto whitespace-pre-wrap bg-gray-100 dark:bg-gray-800 p-2 rounded">
{`// Текущее Unix-время в секундах
Math.floor(Date.now() / 1000);

// Конвертация Unix-времени в дату
const date = new Date(unixTime * 1000);

// Получение Unix-времени из даты
const timestamp = Math.floor(date.getTime() / 1000);`}
            </pre>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
            <div className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-1">Python:</div>
            <pre className="text-xs overflow-x-auto whitespace-pre-wrap bg-gray-100 dark:bg-gray-800 p-2 rounded">
{`import time
import datetime

# Текущее Unix-время
current_time = int(time.time())

# Конвертация Unix-времени в дату
date = datetime.datetime.fromtimestamp(timestamp)

# Получение Unix-времени из даты
timestamp = int(date.timestamp())`}
            </pre>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
            <div className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-1">PHP:</div>
            <pre className="text-xs overflow-x-auto whitespace-pre-wrap bg-gray-100 dark:bg-gray-800 p-2 rounded">
{`// Текущее Unix-время
$current_time = time();

// Конвертация Unix-времени в дату
$date = date('Y-m-d H:i:s', $timestamp);

// Получение Unix-времени из даты
$timestamp = strtotime('2023-04-25 14:30:00');`}
            </pre>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
            <div className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-1">Java:</div>
            <pre className="text-xs overflow-x-auto whitespace-pre-wrap bg-gray-100 dark:bg-gray-800 p-2 rounded">
{`// Текущее Unix-время в секундах
long currentTime = System.currentTimeMillis() / 1000L;

// Конвертация Unix-времени в дату
Date date = new Date(timestamp * 1000L);

// Получение Unix-времени из даты
long timestamp = date.getTime() / 1000L;`}
            </pre>
          </div>
        </div>

        <h3 className="flex items-center gap-2">
          <Database className="w-5 h-5 text-cyan-500" />
          Использование в базах данных и системах
        </h3>
        <p>
          В базах данных и информационных системах Unix-время часто используется для хранения временных меток:
        </p>
        <ul>
          <li><strong>SQL базы данных:</strong> Многие СУБД имеют специальные типы данных для хранения времени (например, TIMESTAMP в PostgreSQL или MySQL).</li>
          <li><strong>Логирование:</strong> В системных журналах и логах часто используется Unix-время для записи времени событий.</li>
          <li><strong>Файловые системы:</strong> Время создания, изменения и доступа к файлам часто хранится как Unix-время.</li>
          <li><strong>Сессии и токены:</strong> Время истечения сессий или токенов аутентификации в веб-приложениях часто хранится в формате Unix-time.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Проблема 2038 года
          </h4>
          <p className="text-yellow-600 dark:text-yellow-400 text-sm mb-3">
            19 января 2038 года в 03:14:07 UTC 32-битные системы столкнутся с переполнением Unix-времени, что может привести к серьезным сбоям. Эта проблема аналогична "проблеме 2000 года" (Y2K).
          </p>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>32-битное знаковое целое число может представлять значения от -2,147,483,648 до 2,147,483,647</li>
            <li>После достижения максимального значения произойдет переполнение до отрицательных чисел</li>
            <li>Системы могут интерпретировать отрицательные значения как даты до 1970 года</li>
            <li>Решением является переход на 64-битное представление времени</li>
            <li>Большинство современных систем уже используют 64-битное представление</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-cyan-500" />
          Часовые пояса и Unix-время
        </h3>
        <p>
          Unix-время всегда указывается в UTC (Coordinated Universal Time), но пользователям обычно показывается время в их местном часовом поясе. Важно понимать различие между UTC и локальным временем при работе с Unix-временем:
        </p>
        <ul>
          <li><strong>Сохранение времени:</strong> Храните время в Unix-формате (UTC) без привязки к конкретному часовому поясу.</li>
          <li><strong>Отображение времени:</strong> При отображении конвертируйте время в локальный часовой пояс пользователя.</li>
          <li><strong>Сравнение времени:</strong> Сравнивайте время в Unix-формате, чтобы избежать проблем с переходом на летнее/зимнее время.</li>
          <li><strong>Россия и часовые пояса:</strong> Россия охватывает 11 часовых поясов, от UTC+2 (Калининград) до UTC+12 (Камчатка), что делает корректную работу с часовыми поясами особенно важной для российских приложений.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-cyan-500" />
          Форматы представления даты и времени
        </h3>
        <p>
          При конвертации Unix-времени в человекочитаемый формат существует несколько стандартных форматов:
        </p>
        <ul>
          <li><strong>ISO 8601:</strong> Международный стандарт представления даты и времени (например, "2023-04-25T14:30:00Z"), широко используемый в программировании и обмене данными.</li>
          <li><strong>RFC 2822:</strong> Формат, используемый в электронной почте и HTTP-заголовках (например, "Tue, 25 Apr 2023 14:30:00 +0000").</li>
          <li><strong>Локализованные форматы:</strong> В разных странах приняты различные форматы отображения даты и времени. В России обычно используется формат "ДД.ММ.ГГГГ ЧЧ:ММ:СС".</li>
          <li><strong>Относительное время:</strong> Представление времени относительно текущего момента (например, "5 минут назад", "вчера", "на прошлой неделе"). Такой формат часто используется в социальных сетях и приложениях для улучшения пользовательского опыта.</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш конвертер поможет вам легко переводить Unix-время в человекочитаемые форматы и обратно, учитывая особенности разных часовых поясов и форматов представления!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;